import { UNIQUES, WORN_SLOTS, featGoal, type UniqueId, type UniqueSlot } from '@somnhold/content';
import type { CampaignState, HeroLegend, HeroState, ItemInstance, SlotFeat, UniqueSlotState } from './types.ts';

/**
 * The unique slots and the panoply (docs/PANOPLIA.md §3, §5). What is worked on a unique piece belongs to its slot: the
 * awakening stage, the forge's pluses, the reforges, the attributes and the gems are the same on every unique piece of a
 * hero's slot (`syncSlot` copies them after each change), so the fights and the screens keep reading the worn piece. The
 * slot keeps one feat bar (whatever piece fills it) and the pieces not worn wait in the panoply, out of the bag.
 */

/** A full bar: the slot's feat is done (docs/PANOPLIA.md §3.1: millionths, no fractions in the sim). */
export const BAR = 1_000_000;

/** Every unique piece a hero holds: worn, in its panoply and (a campaign not moved yet) in the bag. */
export const uniquePieces = (h: HeroState): ItemInstance[] => {
  const out: ItemInstance[] = [];
  for (const w of WORN_SLOTS) {
    const it = h.gear[w];
    if (it?.unique) out.push(it);
  }
  for (const it of h.panoply ?? []) out.push(it);
  for (const it of h.bag) if (it.unique) out.push(it);
  return out;
};

/** What a unique piece shares with the rest of its slot (docs/PANOPLIA.md §3). */
const SHARED = ['awaken', 'plus', 'forged', 'reforged', 'uqBase', 'attrs', 'sockets', 'socket', 'infused'] as const;

/** A copy of a shared field, so two pieces never hold the same object. */
const copyOf = (v: unknown): unknown => {
  if (Array.isArray(v)) return v.map((x) => (x && typeof x === 'object' ? { ...x } : x));
  if (v && typeof v === 'object') return { ...v };
  return v;
};

/** Copies what is worked on `from` to every other unique piece of its slot and class the hero holds. */
export const syncSlot = (h: HeroState, from: ItemInstance): void => {
  if (!from.unique) return;
  const cls = UNIQUES[from.unique].cls;
  const src = from as unknown as Record<string, unknown>;
  for (const it of uniquePieces(h)) {
    if (it === from || !it.unique || it.slot !== from.slot || UNIQUES[it.unique].cls !== cls) continue;
    const dst = it as unknown as Record<string, unknown>;
    for (const k of SHARED) {
      if (src[k] === undefined) delete dst[k];
      else dst[k] = copyOf(src[k]);
    }
  }
};

/** A hero's unique slot, made when its first piece comes (feat I starting; docs/UNICAS.md §2.1). */
export const ensureSlot = (h: HeroState, slot: UniqueSlot, last?: number): UniqueSlotState => {
  const all = (h.uniqueSlots ??= {});
  const st = (all[slot] ??= { feat: { step: 1, bar: 0, rem: 0 } });
  if (last !== undefined && st.last === undefined) st.last = last;
  return st;
};

/** The feat a piece's slot is at (null: no slot, or none left). */
export const slotFeat = (h: HeroState, id: UniqueId): SlotFeat | null => {
  const f = h.uniqueSlots?.[UNIQUES[id].slot]?.feat;
  return f && f.step <= 4 ? f : null;
};

/**
 * Adds what piece `id` did (`amount` of its `goal`) to the slot's bar, the division's rest waiting for the next event of
 * the same piece: the bar is full exactly when the piece alone would have reached its goal. True when the bar is full.
 */
export const addBar = (f: SlotFeat, id: UniqueId, amount: number, goal: number): boolean => {
  if (f.by !== id) {
    f.rem = 0;
    f.by = id;
  }
  const num = f.rem + amount * BAR;
  f.bar += Math.trunc(num / goal);
  f.rem = num % goal;
  return f.bar >= BAR;
};

/** A slot's next feat (its bar back to empty, as a done feat was before: what went over doesn't carry). */
export const nextFeat = (f: SlotFeat): void => {
  f.step += 1;
  f.bar = 0;
  f.rem = 0;
  delete f.by;
};

/** A piece's feat as the screens show it: the slot's step, how far the bar is in this piece's count, and its goal. */
export const featView = (h: HeroState, id: UniqueId): { step: number; n: number; goal: number; bosses: string[] } | null => {
  const f = h.uniqueSlots?.[UNIQUES[id].slot]?.feat;
  if (!f) return null;
  const bosses = h.legends?.[id]?.bosses ?? [];
  if (f.step >= 4) return { step: f.step, n: bosses.length, goal: featGoal(id, 4), bosses };
  const goal = featGoal(id, f.step);
  // With its own rest the count is exact; another piece's rest isn't this one's.
  return { step: f.step, n: Math.trunc((f.bar * goal + (f.by === id ? f.rem : 0)) / BAR), goal, bosses };
};

/**
 * Sets where a piece's feat is, counted in that piece's own goal (`n`; the Legend's bosses for steps 4 and 5), as the
 * feats were kept before the slots: for tests and tools.
 */
export const setFeat = (h: HeroState, id: UniqueId, at: { step: number; n?: number; bosses?: string[] }): void => {
  const st = ensureSlot(h, UNIQUES[id].slot);
  const f: SlotFeat = { step: at.step, bar: 0, rem: 0 };
  if (at.step <= 3 && at.n) {
    const goal = featGoal(id, at.step);
    const num = at.n * BAR;
    f.bar = Math.trunc(num / goal);
    f.rem = num % goal;
    f.by = id;
  }
  st.feat = f;
  if (at.bosses || at.step >= 5) {
    const lg: HeroLegend = { bosses: [...(at.bosses ?? [])] };
    (h.legends ??= {})[id] = lg;
    if (at.step >= 5) lg.done = true;
  }
};

/** Whether a piece's final form is open to it: its own Legend done (docs/PANOPLIA.md §3.2; the slot's stage IV is asked apart). */
export const finalOpen = (h: HeroState, id: UniqueId): boolean => !!h.legends?.[id]?.done;

/* ---------- campaigns from before (2026-11-04) ---------- */

/** A hero's pieces moved onto their slots (§3.3): the bag's uniques into the panoply, each piece's feat into its slot's bar. */
const heroPanoply = (h: HeroState): boolean => {
  if (h.uniqueSlots) return false;
  const pieces = uniquePieces(h);
  if (!pieces.length && !h.feats) return false;
  const inBag = h.bag.filter((x) => x.unique);
  if (inBag.length) {
    h.bag = h.bag.filter((x) => !x.unique);
    h.panoply = [...(h.panoply ?? []), ...inBag];
  }
  for (const it of pieces) {
    const id = it.unique as UniqueId;
    const slot = UNIQUES[id].slot;
    const worn = h.gear[slot] === it;
    const st = ensureSlot(h, slot, worn ? it.id : undefined);
    if (worn) st.last = it.id;
    const old = h.feats?.[id];
    const step = old?.step ?? (it.awaken ?? 0) + 1;
    const f: SlotFeat = { step, bar: 0, rem: 0 };
    if (step <= 3 && old) {
      // The count so far, exactly: the bar fills on the same event it would have before.
      const goal = featGoal(id, step);
      const num = (old.n ?? 0) * BAR;
      f.bar = Math.trunc(num / goal);
      f.rem = num % goal;
      f.by = id;
    }
    st.feat = f;
    // The Legend's bosses go with the piece; a piece past it (or awake to the IV) has its Legend done.
    if (old?.bosses?.length || step >= 5 || (it.awaken ?? 0) >= 4) {
      const lg = ((h.legends ??= {})[id] ??= { bosses: [] });
      for (const b of old?.bosses ?? []) if (!lg.bosses.includes(b)) lg.bosses.push(b);
      if (step >= 5 || (it.awaken ?? 0) >= 4) lg.done = true;
    }
  }
  // A slot no piece of the hero's is in (none left worn or kept) has no last piece yet: the first unique of it found.
  const slots = (h as HeroState).uniqueSlots ?? {};
  for (const it of pieces) {
    const st = slots[UNIQUES[it.unique as UniqueId].slot];
    if (st && st.last === undefined) st.last = it.id;
  }
  delete h.feats;
  return true;
};

/**
 * Campaigns from before the unique slots (docs/PANOPLIA.md §3.3): every hero's (and bench hero's) unique pieces leave
 * the bag for the panoply, and their feats become their slot's. Runs after `ensureUniquePaths` when a campaign loads;
 * a hero that has its slots is left alone, so it is idempotent.
 */
export const ensurePanoply = (s: CampaignState): boolean => {
  let changed = false;
  for (const p of s.players) {
    if (heroPanoply(p.hero)) changed = true;
    for (const b of p.bench ?? []) if (heroPanoply(b.hero)) changed = true;
  }
  if (s.panoply === undefined) {
    s.panoply = 1;
    changed = true;
  }
  return changed;
};

/**
 * The bag as the screens showed it before the panoply's second stage (docs/PANOPLIA.md §11): the bag and, after it, the
 * unique pieces not worn. The «Tus únicas» strip replaces this in the second stage.
 */
export const heldBag = (h: HeroState): ItemInstance[] => (h.panoply?.length ? [...h.bag, ...h.panoply] : h.bag);
